import React, { useEffect, useState } from 'react'
import type { GetServerSidePropsContext } from 'next'
import Cookie from 'cookie-universal'

import { Pages, TRACK_PROPS, TrackContentType } from '@mc/track-event'
import { BogoBanner } from '@mc/bogo'
import type { SessionsResult, SingleSessionResult } from '@mc/api'
import { DiscountModal } from '@mc/promos'
import { paths } from '@mc/paths'
import { TrackingParamsProvider } from '@mc/tracking-context'

import { StickyFooter } from 'components/StickyFooter'
import {
  getDefaultProduct,
  getFullInstructorBioByCampSlug,
  getSessionBySlug,
  getSessions,
  getMarketingRows,
} from 'libs/server/api'
import { useSprig } from 'libs/sprig'
import { redirect } from 'libs/utils'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import type { DefaultProduct } from 'types/bridgeTypes'
import {
  getV2Categories,
  getGrowthClassCategoriesIds,
  getGrowthClassSubcategoriesIds,
  getPrimaryCategoryAndSubcategoryForGrowthClass,
  PrimaryCategoryAndSubcategory,
} from 'libs/server/api/growth'
import { MarketingDataType } from 'types'

import { FooterCTAs } from 'pages/sessions/shared/FooterCTAs'
import { VideoPlayerModal } from 'pages/sessions/shared/VideoPlayerModal'
import { GrowthFooterSection } from 'pages/sessions/shared/GrowthFooterSection/GrowthFooterSection'

import { HeadTag } from './components/HeadTag'
import { SessionCampLandingPageDataProvider } from './components/SessionsLandingPageDataProvider'
import { CampHero } from './components/CampHero/CampHero'
import { CampContent } from './components/CampContent'

import {
  trackCampOverviewTrailerCTAClick,
  trackCampFooterTrailerCTAClick,
  getVideoExtraTrackingInfo,
  trackCampPageViewed,
} from './events'
import { presentSession } from './utils'
import type { Camp, InstructorBioResult } from './types'

export type SessionCampLandingPageProps = {
  session: SingleSessionResult
  annualPass: DefaultProduct
  instructorBio: InstructorBioResult
  sessions: SessionsResult
  primaryCatAndSubcat: PrimaryCategoryAndSubcategory
  classCategoriesIds: number[]
  classSubcategoriesIds: number[]
  marketingRows: MarketingDataType[]
}

export type SessionCampLandingPageData = Omit<
  SessionCampLandingPageProps,
  'primaryCatAndSubcat' | 'classCategoriesIds' | 'classSubcategoriesIds'
> & {
  camp: Camp
}

interface VideoProperty {
  contentId: string
  contentTitle: string
  contentType: TrackContentType
  isOverview: boolean
}

const useTrackCampPageViewed = (
  c: Partial<Camp>,
  annualPass: Pick<DefaultProduct, 'productLtv' | 'pricing'>,
  primaryCategoryAndSubcategory: PrimaryCategoryAndSubcategory,
  classCategoriesIds: number[],
  classSubcategoriesIds: number[],
) => {
  const { id, slug, title } = c

  useEffect(() => {
    if (id && slug && title && primaryCategoryAndSubcategory) {
      trackCampPageViewed({
        id,
        slug,
        title,
        price: annualPass.productLtv?.regular,
        primaryCategoryId: primaryCategoryAndSubcategory?.primaryCategory?.id,
        categoryIds: classCategoriesIds,
        subcategoryIds: classSubcategoriesIds,
        currency: annualPass?.pricing?.currency || 'XX',
      })
    }
  }, [
    annualPass.productLtv?.regular,
    id,
    slug,
    title,
    primaryCategoryAndSubcategory,
    classCategoriesIds,
    classSubcategoriesIds,
    annualPass?.pricing?.currency,
  ])
}

const setPageViewedCookie = (slug: string) => {
  const cookies = Cookie()
  const DAY = 60 * 60 * 24

  const existing = cookies.get('all_cm_pages_viewed') ?? []

  const withQuotes = encodeURIComponent(slug)

  const newCookieValue = Array.from(new Set([withQuotes, ...existing]))

  const cookieOptions = {
    maxAge: DAY * 14,
    path: '/',
  }

  cookies.set('all_cm_pages_viewed', newCookieValue, cookieOptions)
  cookies.set('last_cm_page', slug, cookieOptions)
}

export default function SessionCampLandingPage({
  session,
  annualPass,
  instructorBio,
  sessions,
  primaryCatAndSubcat,
  classCategoriesIds,
  classSubcategoriesIds,
  marketingRows,
}: SessionCampLandingPageProps) {
  const [videoPlayerId, setVideoPlayerId] = useState<string | null>(null)
  const [videoPlayerUuid, setVideoPlayerUuid] = useState<string | null>(null)
  const [videoProperty, setVideoProperty] = useState<VideoProperty | null>(null)

  const camp = presentSession(session)

  useTrackCampPageViewed(
    camp,
    annualPass,
    primaryCatAndSubcat,
    classCategoriesIds,
    classSubcategoriesIds,
  )
  useSprig()

  const onOverviewClick = () => {
    setVideoPlayerId(camp.classOverviewTrailer?.brightcoveVideoId ?? null)
    setVideoPlayerUuid(camp.classOverviewTrailer?.mediaUuid ?? null)
    setVideoProperty({
      contentId: `${camp.id}_${
        camp.classOverviewTrailer?.brightcoveVideoId ??
        camp.classOverviewTrailer?.mediaUuid
      }`,
      contentTitle: 'Camp Class Overview Trailer',
      contentType: 'Camp Trailer',
      isOverview: true,
    })
    trackCampOverviewTrailerCTAClick({
      ...camp,
      contentId: `${camp.id}_${
        camp.classOverviewTrailer?.brightcoveVideoId ??
        camp.classOverviewTrailer?.mediaUuid
      }`,
    })
  }

  const onFooterTrailerCTAClick = () => {
    setVideoPlayerId(camp.trailer?.brightcoveVideoId ?? null)
    setVideoPlayerUuid(camp.trailer?.mediaUuid ?? null)
    setVideoProperty({
      contentId: `${camp.id}_${
        camp.trailer?.brightcoveVideoId ?? camp.trailer?.mediaUuid
      }`,
      contentTitle: 'Camp Class Footer Trailer',
      contentType: 'Camp Trailer',
      isOverview: false,
    })
    trackCampFooterTrailerCTAClick({
      ...camp,
      contentId: `${camp.id}_${
        camp.trailer?.brightcoveVideoId ?? camp.trailer?.mediaUuid
      }`,
    })
  }

  const onVideoPlayerClose = () => {
    setVideoPlayerId(null)
    setVideoProperty(null)
  }

  useEffect(() => {
    setPageViewedCookie(camp.slug)
  }, [camp.slug])

  return (
    <TrackingParamsProvider
      value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-sessions' }}
    >
      <SessionCampLandingPageDataProvider
        data={{
          session,
          camp,
          annualPass,
          instructorBio,
          sessions,
          marketingRows,
        }}
      >
        <DiscountModal />
        <BogoBanner isDark />
        <HeadTag />
        <CampHero />
        <CampContent
          camp={camp}
          session={session}
          onOverviewClick={onOverviewClick}
        />

        <FooterCTAs
          title={'sessions.footer.readyToGetStarted'}
          onTrailerClick={onFooterTrailerCTAClick}
          pageType='class-page'
          hasTrailer={
            !!camp.trailer?.brightcoveVideoId || !!camp.trailer?.mediaUuid
          }
        />
        {videoProperty && (videoPlayerId || videoPlayerUuid) && (
          <VideoPlayerModal
            videoPlayerId={videoPlayerId as string}
            mediaUuid={videoPlayerUuid ?? undefined}
            contentId={videoProperty.contentId}
            contentTitle={videoProperty.contentTitle}
            contentType={videoProperty.contentType}
            onClose={onVideoPlayerClose}
            extraTrackingInfo={getVideoExtraTrackingInfo(
              videoProperty.isOverview,
              {
                ...camp,
                contentId: videoProperty.contentId,
              },
            )}
            pageType='class-page'
          />
        )}
        <GrowthFooterSection location='camp' />
        <div className='mc-theme-dark'>
          <StickyFooter />
        </div>
      </SessionCampLandingPageDataProvider>
    </TrackingParamsProvider>
  )
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const {
    query: { slug: campSlug },
  } = ctx

  if (typeof campSlug !== 'string') {
    throw new Error('Bad request')
  }

  let session
  try {
    session = await getSessionBySlug(ctx, campSlug as string)
  } catch (error) {
    return pageErrorHandler(error)
  }
  if (session.slug !== campSlug) {
    return redirect(ctx, `/sessions/classes/${session.slug}`, {
      queryExclude: ['slug'],
    })
  }

  const data = await getPageData({
    criticalPromises: {
      session: Promise.resolve(session),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      categories: getV2Categories(ctx),
      instructorBio: getFullInstructorBioByCampSlug(ctx, campSlug as string),
      sessions: getSessions(ctx, { allowAltSet: true }),
      primaryCatAndSubcat: getPrimaryCategoryAndSubcategoryForGrowthClass(
        ctx,
        campSlug as string,
      ),
      classCategoriesIds: getGrowthClassCategoriesIds(ctx, campSlug as string),
      classSubcategoriesIds: getGrowthClassSubcategoriesIds(
        ctx,
        campSlug as string,
      ),
      marketingRows: getMarketingRows(ctx),
    },
  })

  const { props } = data as { props?: { error?: { statusCode: number } } }

  // Content-curation exclusions handle
  if (props?.error?.statusCode === 404) {
    return {
      redirect: {
        destination: paths.root,
        permanent: false,
      },
    }
  }

  return data
}

SessionCampLandingPage.pageSettings = {
  rumLabel: 'SessionsPrePaywallCoursePage',
  analyticsLabel: Pages.SESSION_COURSE_MARKETING_PAGE,
  theme: 'mc-theme-sessions',
  showPromo: true,
}
